import { FC, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { ChevronDown, ChevronRight, Eye, EyeOff, GraduationCap, Users } from 'lucide-react';
import { Class } from '../../models/Class';
import { getClassCardData } from '../../businessLogicService/getClassCardData';

interface Props {
  hiddenClasses: Class[];
  onUnhide: (cls: Class) => void;
}

const ClassesRosterHiddenSection: FC<Props> = ({ hiddenClasses, onUnhide }) => {
  const [expanded, setExpanded] = useState(false);

  const items = useMemo(
    () =>
      hiddenClasses
        .map(cls => ({ cls, data: getClassCardData(cls) }))
        .sort((a, b) => a.data.name.localeCompare(b.data.name)),
    [hiddenClasses],
  );

  if (!hiddenClasses.length) return null;

  return (
    <div className="nc_hidden-section flex flex-column mt-3">
      <button
        type="button"
        className="nc_hidden-section__header flex align-items-center gap-sm"
        onClick={() => setExpanded(value => !value)}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />}
        <EyeOff size={16} aria-hidden />
        <span className="font-semibold">Hidden classes · {hiddenClasses.length}</span>
      </button>

      {expanded && (
        <div className="flex flex-column gap-md mt-3">
          <p className="m-0 tw-text-neutral-700">
            Hidden classes are removed from your list but keep all their data. Unhide one anytime.
          </p>
          <div className="grid">
            {items.map(({ cls, data }) => (
              <div key={data.id} className="col-12 lg:col-6">
                <div className="nc_hidden-item flex flex-column gap-md md:flex-row md:justify-content-between md:align-items-center h-full">
                  <div className="flex flex-column">
                    <span className="font-semibold tw-text-lg">{data.name}</span>
                    <div className="flex align-items-center gap-lg mt-1 font-semibold ">
                      {data.gradeLabel && (
                        <span className="flex align-items-center gap-xs">
                          <GraduationCap size={14} aria-hidden />
                          {data.gradeLabel}
                        </span>
                      )}
                      <span className="flex align-items-center gap-xs">
                        <Users size={14} aria-hidden />
                        {data.studentCount} students
                      </span>
                    </div>
                  </div>
                  <Button
                    label="Unhide"
                    icon={<Eye size={14} className="mr-2" />}
                    outlined
                    rounded
                    onClick={() => onUnhide(cls)}
                    aria-label={`Unhide ${data.name}`}
                    className="flex-shrink-0"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesRosterHiddenSection;
